import { Badge } from "@volca/ui/components/ui/badge";
import { Button } from "@volca/ui/components/ui/button";
import { Card } from "@volca/ui/components/ui/card";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
} from "@volca/ui/components/ui/empty";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@volca/ui/components/ui/table";
import { Text } from "@volca/ui/components/ui/text";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import {
  roleBadgeVariant as roleBadgeColor,
  roleLabel,
} from "@/user/constants";
import { AccessChips } from "./access-chips";
import type { WorkbookAccessSettings, WorkingAccess } from "./types";
import { assignmentsFor } from "./utils";

interface PeopleTableProps {
  settings: WorkbookAccessSettings;
  working: WorkingAccess;
  selectedId: string | null;
  onSelect: (userId: string) => void;
  // While a save runs, nobody else can be picked: finishing it closes the drawer.
  disabled?: boolean;
}

export const PeopleTable = ({
  settings,
  working,
  selectedId,
  onSelect,
  disabled = false,
}: PeopleTableProps) => (
  <Card className="gap-0 py-0">
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Person</TableHead>
          <TableHead>Role</TableHead>
          <TableHead>Access</TableHead>
          <TableHead>
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {settings.users.length === 0 && (
          <TableRow>
            <TableCell colSpan={4}>
              <Empty className={emptyStateVariants({ layout: "inline" })}>
                <EmptyHeader>
                  <EmptyDescription>
                    No people yet. Add them in Users first.
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            </TableCell>
          </TableRow>
        )}
        {settings.users.map((person) => {
          const rows = assignmentsFor(working.assignments, person.id);
          return (
            <TableRow
              key={person.id}
              data-state={person.id === selectedId ? "selected" : undefined}
              className={disabled ? undefined : "cursor-pointer"}
              onClick={() => {
                if (!disabled) onSelect(person.id);
              }}
            >
              <TableCell className="py-3">
                <Stack align="start" gap={0}>
                  <Stack direction="row" gap={2} align="center">
                    <Text variant="label">{person.name}</Text>
                    {person.removed && (
                      <Badge variant="secondary" size="sm">
                        Removed
                      </Badge>
                    )}
                  </Stack>
                  <Text variant="caption" tone="muted">
                    {person.email}
                  </Text>
                </Stack>
              </TableCell>
              <TableCell className="py-3">
                <Badge
                  variant="secondary"
                  color={roleBadgeColor(person.role)}
                  size="sm"
                >
                  {roleLabel(person.role)}
                </Badge>
              </TableCell>
              <TableCell className="py-3 whitespace-normal">
                <AccessChips
                  rows={rows}
                  keySettings={working.keySettings}
                  areas={settings.access_keys}
                />
              </TableCell>
              <TableCell className="py-3 pr-4 text-right">
                <Button
                  variant="outline"
                  size="sm"
                  aria-label={`Edit access for ${person.name}`}
                  disabled={disabled}
                  onClick={(event) => {
                    event.stopPropagation();
                    onSelect(person.id);
                  }}
                >
                  Edit
                </Button>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </Card>
);
